import { privateAgent } from "@lib/Requests/AuthRequests";
import { PaigeResArr, PaigeResObj } from "@lib/Requests/types";
import { routesName } from "@lib/RoutesName";
import { AddBehaviorCategoryProps, AddBehaviorProps, BehaviorCategoryProps, BehaviorProps, EditBehaviorCategoryProps, EditBehaviorProps } from "@lib/Types";
import { useSnackbar } from "notistack";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useState, useEffect } from "react";
import { handleAxiosError } from "@lib/Requests/errorHandler";

const behaviorCategoryRequest = async () => {
  try {
    const { data } = await privateAgent.get<PaigeResArr<BehaviorCategoryProps>>(
      routesName.BehaviorCategoryRoute({}).get_behavior_category
    );

    if (data.status === 200) {
      const results = data.result.items;
      return results;
    } else {
      throw new Error(data.error || "Failed to fetch behaviors");
    }
  } catch (error: any) {
    console.error("Error fetching behaviors:", error.message);
    handleAxiosError(error, 'Failed to fectch behavior categories')
  }
};

const updateBehaviorCategoryRequest = async ({ id, data }: { id: string; data: EditBehaviorCategoryProps }) => {
  try {
    const res = await privateAgent.put<PaigeResObj<BehaviorCategoryProps>>(
      routesName.BehaviorCategoryRoute({ id }).update_behavior_category,
      data
    );
    if (res.data && res.data.status === 200) {
      return res.data;
    } else {
      throw new Error(res.data.error || "Failed to update behavior");
    }
  } catch (error) {
    handleAxiosError(error, 'Failed to update behavior categories')
  }
};

const addBehaviorCategoryRequest = async ({ id, data }: { id: string; data: AddBehaviorCategoryProps }) => {
  try {
    const res = await privateAgent.post<PaigeResObj<BehaviorCategoryProps>>(
      routesName.BehaviorCategoryRoute({ id }).add_behavior_category,
      data
    );
    if (res.data && res.data.status === 201) {
      return res.data;
    } else {
      throw new Error(res.data.error || "Failed to update behavior");
    }
  } catch (error) {
    handleAxiosError(error, 'Failed to add behvior categories')
  }
};

const deleteBehaviorCategoryRequest = async (id: string) => {
  try {
    const res = await privateAgent.delete<PaigeResObj<BehaviorCategoryProps>>(
      routesName.BehaviorCategoryRoute({ id }).delete_behavior_category
    );
    if (res.data && res.data.status === 200) {
      return res.data;
    } else {
      throw new Error(res.data.error || "Failed to delete behavior");
    }
  } catch (error) {
    handleAxiosError(error, 'Failed to delete behvior categories')
  }
};

export const useBehaviorCategory = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [filteredData, setFilteredData] = useState<BehaviorCategoryProps[]>([]);
  const [searchQueries, setSearchQueries] = useState<{ searchQuery: string; behaviorCategoryId: string }>({
    searchQuery: "",
    behaviorCategoryId: "0",
  });

  const { isLoading, data, error } = useQuery({
    queryKey: ["BehaviorCategoryQuery"],
    queryFn: behaviorCategoryRequest,
    suspense: true,
    onSuccess: (data) => {
      if(data)
        setFilteredData(data);
    },
    onError: (error: any) => {
      console.error("Error fetching behaviors:", error.message);
      enqueueSnackbar(error.message ?? "An error occurred", { variant: "error" });
    },
  });

  const updateBehaviorCatMutation = useMutation(updateBehaviorCategoryRequest, {
    onSuccess: () => {
      queryClient.invalidateQueries("BehaviorCategoryQuery");
      enqueueSnackbar("Behavior category updated successfully", { variant: "success" });
    },
    onError: (error: any) => {
      console.error("Error updating behavior:", error.message);
      enqueueSnackbar(error.message ?? "An error occurred", { variant: "error" });
    },
  });

  const addBehaviorCatMutation = useMutation(addBehaviorCategoryRequest, {
    onSuccess: () => {
      queryClient.invalidateQueries("BehaviorCategoryQuery");
      enqueueSnackbar("Behavior category added successfully", { variant: "success" });
    },
    onError: (error: any) => {
      console.error("Error adding behavior:", error.message);
      enqueueSnackbar(error.message ?? "An error occurred", { variant: "error" });
    },
  });

  const deleteBehaviorCatMutation = useMutation(deleteBehaviorCategoryRequest, {
    onSuccess: () => {
      queryClient.invalidateQueries("BehaviorCategoryQuery");
      enqueueSnackbar("Behavior category deleted successfully", { variant: "success" });
    },
    onError: (error: any) => {
      console.error("Error deleting behavior:", error.message);
      enqueueSnackbar(error.message ?? "An error occurred", { variant: "error" });
    },
  });

  useEffect(() => {
    if (data) {
      const filtered = data.filter((item: BehaviorCategoryProps) => {
        const hasBehavior = item.name.toLowerCase().includes(searchQueries.searchQuery.toLowerCase());

        let toFiltered = true;
        if (searchQueries.searchQuery !== "") {
          toFiltered = toFiltered && hasBehavior;
        }
        return toFiltered;
      });
      setFilteredData(filtered);
    }
  }, [data, searchQueries]);

  const handleSearchQuery = (searchQuery: { searchQuery: string; behaviorCategoryId: string }) => {
    setSearchQueries(searchQuery);
  };

  return { isLoading, data, filteredData, setFilteredData, error, handleSearchQuery, updateBehaviorCatMutation, addBehaviorCatMutation, deleteBehaviorCatMutation };
};

